/**
 * 计费重构（2026-06-28）— admin 钱包计费仪表板（订阅视角已退役）。
 *
 * 顶部 KPI：总余额 / 累计充值 / 实收消费 / 真实成本 / 毛利 / 钱包数。
 * 下方表：每用户当前余额 + 累计充值 / 消费。Read-only —— 退款 / 手动调整
 * 走 Stripe Dashboard 或后续运营工具。
 */
import { useEffect, useState } from 'react';
import { api } from '../api';

type Data = Awaited<ReturnType<typeof api.adminBilling>>;

const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export function AdminBilling() {
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .adminBilling()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div style={{ color: 'var(--red, #d23f3f)', fontSize: 13 }}>
        Failed to load billing data: {err}
      </div>
    );
  }
  if (!data) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
  }

  // 毛利率 = 毛利 / 真实成本（默认 markup 100% → 约 100%）。
  const marginPct =
    data.real_cost_cents > 0
      ? Math.round((data.gross_profit_cents / data.real_cost_cents) * 100)
      : null;

  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: '2-digit',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ fontSize: 13.5, color: 'var(--ink-1)' }}>
      {/* KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 14,
          marginBottom: 28,
        }}
      >
        <KpiCard
          label="Outstanding balance"
          value={usd(data.total_balance_cents)}
          sub={`${data.wallet_count} wallet${data.wallet_count === 1 ? '' : 's'}`}
        />
        <KpiCard label="Topped up" value={usd(data.total_topup_cents)} sub="lifetime" tone="green" />
        <KpiCard label="Revenue (spent)" value={usd(data.total_spend_cents)} sub="billed to wallets" tone="green" />
        <KpiCard label="Provider cost" value={usd(data.real_cost_cents)} sub="real LLM cost" tone="amber" />
        <KpiCard
          label="Gross profit"
          value={usd(data.gross_profit_cents)}
          sub={marginPct == null ? 'revenue − cost' : `${marginPct}% margin`}
          tone="green"
        />
        <KpiCard label="Signup bonuses" value={usd(data.total_bonus_cents)} sub="given away" tone="amber" />
      </div>

      {/* Per-user wallet table */}
      {data.users.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          No wallets yet. The table will populate as users sign up (each gets a starting balance).
        </div>
      ) : (
        <div style={{ border: '1px solid var(--rule)', borderRadius: 4, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: 'var(--paper-1)' }}>
                <Th>User</Th>
                <Th align="right">Balance</Th>
                <Th align="right">Topped up</Th>
                <Th align="right">Spent</Th>
                <Th align="right">Updated</Th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.user_id} style={{ borderTop: '1px solid var(--rule)' }}>
                  <Td>
                    <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>
                      {u.email ?? <em>(no email)</em>}
                    </div>
                    {u.display_name && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{u.display_name}</div>
                    )}
                  </Td>
                  <Td align="right">
                    <span
                      style={{
                        fontWeight: 600,
                        color: u.balance_cents < 100 ? 'var(--amber)' : 'var(--ink-0)',
                      }}
                    >
                      {usd(u.balance_cents)}
                    </span>
                  </Td>
                  <Td align="right">{usd(u.topup_cents)}</Td>
                  <Td align="right">{usd(u.spend_cents)}</Td>
                  <Td align="right">
                    <span style={{ color: 'var(--ink-3)', fontSize: 11.5 }}>{fmtDate(u.updated_at)}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        Revenue = total spent from wallets (markup included). Provider cost = real LLM cost from
        usage_events. Gross profit = revenue − provider cost. Outstanding balance is prepaid credit
        not yet spent (a liability). For exact payment records see Stripe Dashboard.
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'amber' | 'green' | 'red';
}) {
  const accent =
    tone === 'amber'
      ? 'var(--amber)'
      : tone === 'green'
      ? 'var(--green)'
      : tone === 'red'
      ? 'var(--red, #d23f3f)'
      : 'var(--ink-3)';
  return (
    <div
      style={{
        padding: '14px 16px',
        background: 'var(--paper-1)',
        border: '1px solid var(--rule)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 4,
      }}
    >
      <div
        className="mono"
        style={{ fontSize: 10.5, letterSpacing: '.12em', color: 'var(--ink-3)', marginBottom: 6 }}
      >
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--ink-0)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className="mono"
      style={{
        fontSize: 10,
        letterSpacing: '.12em',
        color: 'var(--ink-3)',
        padding: '8px 10px',
        textAlign: align ?? 'left',
        fontWeight: 700,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ padding: '10px', verticalAlign: 'top', textAlign: align ?? 'left' }}>{children}</td>;
}
